import { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { supabase } from "@/integrations/supabase/client";
import { makeWebhook } from "@/lib/makeWebhook.functions";
import { useQuery } from "@tanstack/react-query";

type Message = { role: "user" | "assistant"; content: string };

interface CollectedInfo {
  name?: string;
  email?: string;
  phone?: string;
}

const CHAT_URL = "/api/hvac-chat";
const SCHEDULE_URL = "/contact";

const quickQuestions = [
  "What roofing services do you offer?",
  "What areas in MN do you serve?",
  "How do I get a free estimate?",
  "Do you handle storm damage?",
];

const emailPattern = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/;
const phonePattern = /(?:\+?1[-.\s]?)?\(?[0-9]{3}\)?[-.\s]?[0-9]{3}[-.\s]?[0-9]{4}/;

const urlLabels: Record<string, string> = {
  "/contact": "Get a Free Estimate",
  "/services/roof-replacement": "Roof Replacement",
  "/services/roof-repair": "Roof Repair",
  "/services/siding": "Siding",
  "/services/windows": "Windows",
  "/services/gutters": "Gutters",
  "/about": "Our Team",
};

const splitIntoChunks = (text: string): string[] => {
  if (text.length <= 150) return [text];
  const midPoint = Math.floor(text.length / 2);
  const sentenceEnders = /[.!?]\s+/g;
  let bestBreak = -1;
  let match;
  while ((match = sentenceEnders.exec(text)) !== null) {
    if (match.index + match[0].length <= midPoint + 50) bestBreak = match.index + match[0].length;
  }
  if (bestBreak > 30 && bestBreak < text.length - 30) return [text.slice(0, bestBreak).trim(), text.slice(bestBreak).trim()];
  return [text];
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const ChatBubble = () => {
  const { data: chatSettings } = useQuery({
    queryKey: ['chatbot-settings'],
    queryFn: async () => {
      const { data, error } = await supabase.from('site_settings').select('key, value').in('key', ['chatbot_popup_text']);
      if (error) throw error;
      const settingsMap: Record<string, string> = {};
      data?.forEach((s) => { settingsMap[s.key] = s.value; });
      return settingsMap;
    },
    staleTime: 1000 * 60 * 5,
  });

  const popupText = chatSettings?.['chatbot_popup_text'] || "Hi! 👋 Need help with roofing, siding, or storm damage? I'm here to answer your questions!";

  const [isOpen, setIsOpen] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupDismissed, setPopupDismissed] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    { role: "assistant", content: "Hi! 👋 I can help answer questions about our roofing, siding, and exterior services. How can I help you today?" },
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [collectedInfo, setCollectedInfo] = useState<CollectedInfo>({});
  const [leadSaved, setLeadSaved] = useState(false);
  const [webhookSent, setWebhookSent] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isTyping, setIsTyping] = useState(false);
  const abandonmentTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());

  // Read sessionStorage after hydration — reading it in the useState
  // initializer crashes SSR ("sessionStorage is not defined").
  useEffect(() => {
    if (sessionStorage.getItem('chatBubbleDismissed') === 'true') setIsDismissed(true);
  }, []);

  useEffect(() => {
    if (popupDismissed || isOpen || isDismissed) return undefined;
    const timer = setTimeout(() => { if (!isOpen && !popupDismissed && !isDismissed) setShowPopup(true); }, 4000);
    return () => clearTimeout(timer);
  }, [isOpen, popupDismissed, isDismissed]);

  useEffect(() => { if (isOpen) setShowPopup(false); }, [isOpen]);

  const handlePopupClick = () => { setShowPopup(false); setPopupDismissed(true); setIsOpen(true); };
  const dismissPopup = (e: React.MouseEvent) => { e.stopPropagation(); setShowPopup(false); setPopupDismissed(true); };
  const handleDismissBubble = (e: React.MouseEvent) => { e.stopPropagation(); setIsOpen(false); setShowPopup(false); setIsDismissed(true); sessionStorage.setItem('chatBubbleDismissed', 'true'); };
  const handleRestoreBubble = () => { setIsDismissed(false); sessionStorage.removeItem('chatBubbleDismissed'); };

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); };
  useEffect(() => { scrollToBottom(); }, [messages, isTyping]);

  useEffect(() => {
    if (!isOpen) return;
    const checkAbandonment = () => {
      const timeSinceActivity = Date.now() - lastActivityRef.current;
      if (timeSinceActivity >= 60000 && !webhookSent && (collectedInfo.email || collectedInfo.phone || collectedInfo.name)) {
        const conversationSummary = messages.slice(-6).map((m) => `${m.role}: ${m.content}`).join("\n");
        const nameParts = (collectedInfo.name || "").trim().split(/\s+/);
        makeWebhook({ data: { firstName: nameParts[0] || "", lastName: nameParts.slice(1).join(" ") || "", email: collectedInfo.email || "", phone: collectedInfo.phone || "", address: "", city: "", state: "", zipCode: "", source: "chatbot_abandoned", conversationSummary } }).then(() => setWebhookSent(true)).catch(console.error);
      }
    };
    abandonmentTimerRef.current = setInterval(checkAbandonment, 15000);
    return () => { if (abandonmentTimerRef.current) clearInterval(abandonmentTimerRef.current); };
  }, [isOpen, webhookSent, collectedInfo, messages]);

  const updateActivity = useCallback(() => { lastActivityRef.current = Date.now(); }, []);

  const extractContactInfo = (text: string, currentInfo: CollectedInfo): CollectedInfo => {
    const newInfo = { ...currentInfo };
    const emailMatch = text.match(emailPattern);
    if (emailMatch) newInfo.email = emailMatch[0];
    const phoneMatch = text.match(phonePattern);
    if (phoneMatch) newInfo.phone = phoneMatch[0];
    const namePatterns = [/(?:I'm|I am|My name is|This is|It's|Call me)\s+([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)/i, /^([A-Z][a-z]+(?:\s+[A-Z][a-z]+)?)$/];
    for (const pattern of namePatterns) {
      const nameMatch = text.match(pattern);
      if (nameMatch && nameMatch[1] && nameMatch[1].length > 1 && nameMatch[1].length < 50) {
        const notNames = ["yes", "no", "maybe", "thanks", "thank", "hello", "hi", "hey", "ok", "okay"];
        if (!notNames.includes(nameMatch[1].toLowerCase())) newInfo.name = nameMatch[1];
      }
    }
    return newInfo;
  };

  const sendToWebhook = useCallback(async (info: CollectedInfo, conversationSummary: string) => {
    if (webhookSent || (!info.email && !info.phone)) return;
    try {
      const nameParts = (info.name || "").trim().split(/\s+/);
      await makeWebhook({ data: { firstName: nameParts[0] || "", lastName: nameParts.slice(1).join(" ") || "", email: info.email || "", phone: info.phone || "", address: "", city: "", state: "", zipCode: "", source: "chatbot", conversationSummary } });
      setWebhookSent(true);
    } catch (error) { console.error("Error sending to webhook:", error); }
  }, [webhookSent]);

  const saveLead = async (info: CollectedInfo, conversationSummary: string) => {
    if (leadSaved || (!info.email && !info.phone)) return;
    try {
      const response = await fetch(CHAT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']}` },
        body: JSON.stringify({ messages: [], saveLead: { name: info.name, email: info.email, phone: info.phone, conversationSummary } }),
      });
      if (response.ok) { setLeadSaved(true); sendToWebhook(info, conversationSummary); }
    } catch (error) { console.error("Error saving lead:", error); }
  };

  const streamChat = async (userMessages: Message[]) => {
    const resp = await fetch(CHAT_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${import.meta.env['VITE_SUPABASE_PUBLISHABLE_KEY']}` },
      body: JSON.stringify({ messages: userMessages }),
    });
    if (!resp.ok) { const errorData = await resp.json().catch(() => ({})); throw new Error(errorData.error || "Failed to get response"); }
    if (!resp.body) throw new Error("No response body");

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = "";
    let fullResponse = "";

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      textBuffer += decoder.decode(value, { stream: true });
      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf("\n")) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);
        if (line.endsWith("\r")) line = line.slice(0, -1);
        if (line.startsWith(":") || line.trim() === "") continue;
        if (!line.startsWith("data: ")) continue;
        const jsonStr = line.slice(6).trim();
        if (jsonStr === "[DONE]") break;
        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content as string | undefined;
          if (content) fullResponse += content;
        } catch { textBuffer = line + "\n" + textBuffer; break; }
      }
    }

    const chunks = splitIntoChunks(fullResponse);
    for (let i = 0; i < chunks.length; i++) {
      const chunk = chunks[i] ?? "";
      setIsTyping(true);
      await delay(Math.min(chunk.length * 25, 1500));
      setIsTyping(false);
      setMessages(prev => [...prev, { role: "assistant", content: chunk }]);
      if (i < chunks.length - 1) await delay(400);
    }
  };

  const handleSend = async (messageText?: string) => {
    const text = messageText || input.trim();
    if (!text || isLoading) return;
    updateActivity();
    const newInfo = extractContactInfo(text, collectedInfo);
    if (newInfo.name !== collectedInfo.name || newInfo.email !== collectedInfo.email || newInfo.phone !== collectedInfo.phone) {
      setCollectedInfo(newInfo);
      if ((newInfo.email || newInfo.phone) && !leadSaved) {
        const conversationSummary = messages.slice(-6).map((m) => `${m.role}: ${m.content}`).join("\n");
        saveLead(newInfo, conversationSummary);
      }
    }
    const userMessage: Message = { role: "user", content: text };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setInput("");
    setIsLoading(true);
    try { await streamChat(newMessages); } catch (error) {
      console.error("Chat error:", error);
      setMessages((prev) => [...prev, { role: "assistant", content: "I'm having trouble connecting right now. Please use our contact form for immediate assistance!" }]);
    } finally { setIsLoading(false); }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const cleanMarkdown = (text: string): string => {
    return text.replace(/\*\*([^*]+)\*\*/g, '$1').replace(/__([^_]+)__/g, '$1').replace(/\*([^*]+)\*/g, '$1').replace(/_([^_]+)_/g, '$1').replace(/^\s*[\*\-•]\s+/gm, '• ').replace(/^\s*\d+\.\s+/gm, '').replace(/\s+/g, ' ').trim();
  };

  const renderMessage = (content: string) => {
    const cleanedContent = cleanMarkdown(content);
    const urlPattern = /(https?:\/\/[^\s,)]+)/g;
    const parts = cleanedContent.split(urlPattern);
    return parts.map((part, idx) => {
      if (part.match(urlPattern)) {
        const cleanUrl = part.replace(/[.,;:!?]+$/, '');
        const trailingPunctuation = part.slice(cleanUrl.length);
        let label = urlLabels[cleanUrl];
        if (!label) {
          const urlPath = cleanUrl.replace(/https?:\/\/[^/]+/, '');
          if (urlPath && urlPath !== '/') { label = urlPath.split('/').filter(Boolean).pop()?.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) || 'View Page'; } else { label = 'Visit Website'; }
        }
        return (<span key={idx}><a href={cleanUrl} target="_blank" rel="noopener noreferrer" className="text-secondary font-medium underline hover:text-secondary/80">{label}</a>{trailingPunctuation}</span>);
      }
      return part;
    });
  };

  if (isDismissed) {
    return (
      <button
        onClick={handleRestoreBubble}
        className="fixed bottom-3 right-3 z-50 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted/80 hover:bg-muted text-muted-foreground hover:text-foreground text-xs font-medium transition-all duration-200 shadow-xs border border-border/50 backdrop-blur-sm"
        aria-label="Open chat"
      >
        <MessageCircle className="w-3.5 h-3.5" />
        Chat with us
      </button>
    );
  }

  return (
    <div className="fixed bottom-4 sm:bottom-6 right-4 sm:right-6 z-50 flex flex-col items-end gap-3">
      <div className={cn("absolute bottom-16 right-0 w-[calc(100vw-2rem)] sm:w-[360px] max-w-[360px] bg-card rounded-2xl shadow-2xl border border-border overflow-hidden transition-all duration-300", isOpen ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 translate-y-4 pointer-events-none")}>
        <div className="bg-primary p-4 text-primary-foreground">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <MessageCircle className="w-5 h-5 text-secondary-foreground" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Kodiak Structures & Exteriors</h3>
                <p className="text-xs opacity-90">Here to help!</p>
              </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/10 rounded-lg transition-colors" aria-label="Close chat">
              <X className="w-5 h-5" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="h-80 overflow-y-auto p-4 space-y-3 bg-background">
          {messages.map((msg, idx) => (
            <div key={idx} className={cn("flex animate-in fade-in slide-in-from-bottom-2 duration-300", msg.role === "user" ? "justify-end" : "justify-start")}>
              <div className={cn("max-w-[85%] rounded-2xl px-4 py-2 text-sm", msg.role === "user" ? "bg-secondary text-secondary-foreground rounded-br-md" : "bg-muted text-foreground rounded-bl-md")}>
                {msg.role === "assistant" ? renderMessage(msg.content) : msg.content}
              </div>
            </div>
          ))}
          {(isLoading || isTyping) && (
            <div className="flex justify-start animate-in fade-in duration-200">
              <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3">
                <div className="flex gap-1">
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                  <span className="w-2 h-2 bg-muted-foreground/50 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {messages.length <= 2 && (
          <div className="px-4 pb-2 flex flex-wrap gap-2">
            {quickQuestions.map((q, idx) => (
              <button key={idx} onClick={() => handleSend(q)} className="text-xs bg-muted hover:bg-muted/80 text-foreground px-3 py-1.5 rounded-full transition-colors">{q}</button>
            ))}
          </div>
        )}

        <div className="p-4 border-t border-border bg-card">
          <div className="flex gap-2">
            <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Type your message..." className="flex-1" disabled={isLoading} />
            <Button onClick={() => handleSend()} size="icon" className="bg-secondary hover:bg-secondary/90 text-secondary-foreground" disabled={isLoading || !input.trim()} aria-label="Send message">
              <Send className="w-4 h-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>

      {showPopup && !isOpen && (
        <div onClick={handlePopupClick} className="absolute bottom-16 right-0 mb-2 w-64 bg-card rounded-xl shadow-xl border border-border p-4 cursor-pointer animate-fade-in">
          <button onClick={dismissPopup} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground transition-colors" aria-label="Dismiss popup">
            <X className="w-4 h-4" aria-hidden="true" />
          </button>
          <p className="text-sm text-foreground pr-4">{popupText}</p>
          <div className="absolute bottom-0 right-6 translate-y-1/2 rotate-45 w-3 h-3 bg-card border-r border-b border-border" />
        </div>
      )}

      <div className="relative group">
        <button onClick={() => setIsOpen(!isOpen)} className={cn("w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ring-2 ring-white ring-offset-2 ring-offset-transparent", isOpen ? "bg-muted text-foreground" : "bg-secondary text-secondary-foreground hover:scale-110")} aria-label={isOpen ? "Close chat" : "Open chat"}>
          {isOpen ? <X className="w-6 h-6" aria-hidden="true" /> : <MessageCircle className="w-6 h-6" aria-hidden="true" />}
        </button>
        {!isOpen && (
          <button onClick={handleDismissBubble} className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-foreground text-background rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md hover:bg-foreground/80" aria-label="Hide chat bubble">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>
    </div>
  );
};

// Instant Estimate button component
export const ScheduleButton = () => {
  const [shouldSheen, setShouldSheen] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [settings, setSettings] = useState({ visible: true, text: 'Get An Instant Estimate', link: 'https://form.fillout.com/t/ow9LbRCdNpus', bgColor: '#b91c1c', textColor: '#ffffff' });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase.from('site_settings').select('key, value').in('key', ['sidebar_tab_visible', 'sidebar_tab_text', 'sidebar_tab_link', 'sidebar_tab_bg_color', 'sidebar_tab_text_color']);
        if (error) throw error;
        const newSettings = { visible: true, text: 'Get An Instant Estimate', link: 'https://form.fillout.com/t/ow9LbRCdNpus', bgColor: '#b91c1c', textColor: '#ffffff' };
        data?.forEach((setting) => {
          switch (setting.key) {
            case 'sidebar_tab_visible': newSettings.visible = setting.value !== 'false'; break;
            case 'sidebar_tab_text': if (setting.value) newSettings.text = setting.value; break;
            case 'sidebar_tab_link': if (setting.value) newSettings.link = setting.value; break;
            case 'sidebar_tab_bg_color': if (setting.value) newSettings.bgColor = setting.value; break;
            case 'sidebar_tab_text_color': if (setting.value) newSettings.textColor = setting.value; break;
          }
        });
        setSettings(newSettings);
      } catch (error) { console.error('Error fetching sidebar tab settings:', error); } finally { setIsLoading(false); }
    };
    fetchSettings();
  }, []);

  useEffect(() => { const hidden = sessionStorage.getItem('estimateButtonHidden'); if (hidden === 'true') setIsHidden(true); }, []);

  useEffect(() => {
    if (isHidden || !settings.visible) return;
    const interval = setInterval(() => { setShouldSheen(true); setTimeout(() => setShouldSheen(false), 1000); }, 10000);
    return () => clearInterval(interval);
  }, [isHidden, settings.visible]);

  const handleDismiss = (e: React.MouseEvent) => { e.preventDefault(); e.stopPropagation(); setIsHidden(true); sessionStorage.setItem('estimateButtonHidden', 'true'); };

  if (isLoading || isHidden || !settings.visible) return null;

  return (
    <div className="fixed left-0 top-1/2 -translate-y-1/2 z-50 group">
      <a href={settings.link} target="_blank" rel="noopener noreferrer" className={cn("flex items-center gap-2 px-3 py-4 rounded-r-lg shadow-lg hover:translate-x-1 transition-all duration-300 font-semibold text-sm", shouldSheen && "animate-sheen")} style={{ writingMode: 'vertical-rl', textOrientation: 'mixed', backgroundColor: settings.bgColor, color: settings.textColor }}>
        {settings.text}
      </a>
      <button onClick={handleDismiss} className="absolute -top-2 -right-2 w-5 h-5 bg-foreground text-background rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200 shadow-md hover:bg-foreground/80" aria-label="Hide estimate button">
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};
